import { useState, useEffect, useCallback } from 'react'
import { getMessage } from '../../../utils/messageUtils'
import { AlertTime, type recipe } from '../../../utils/recipeUtils'
import './CalendarPopupContent.scss'
import type { MealType } from '../../../utils/calendarUtils'
import AlertInput from '../../PopupForm/AlertInput'
import type { User } from '../../../utils/loginUtils'

interface CalendarPopupContentProps {
  recipeId: number | null
  type: MealType
  recipeFromTupper: boolean
  recipeAlert: string | null
  recipeAlertTime: AlertTime | null
  existingRecipes: recipe[] | undefined
  user: User
  setPopupOnOk: React.Dispatch<React.SetStateAction<() => void>>
  storeRecipe: (
    newRecipeId: number,
    fromTupper: boolean,
    alert: string | null,
    alertTime: AlertTime | null
  ) => Promise<void>
  closePopup: () => void
  isMobile: boolean
}

function CalendarPopupContent({
  recipeId,
  type,
  recipeFromTupper,
  recipeAlert,
  recipeAlertTime,
  existingRecipes,
  user,
  setPopupOnOk,
  storeRecipe,
  closePopup,
  isMobile,
}: CalendarPopupContentProps) {
  const [selectedRecipeId, setSelectedRecipeId] = useState<number | null>(
    recipeId
  )
  const [selectedRecipeFromTupper, setSelectedRecipeFromTupper] =
    useState(recipeFromTupper)
  const [alert, setAlert] = useState(recipeAlert || '')
  const [alertTime, setAlertTime] = useState<AlertTime>(
    recipeAlertTime || AlertTime.SAME_DAY_MORNING
  )

  const handleOk = useCallback(async () => {
    if (selectedRecipeId !== null) {
      await storeRecipe(
        selectedRecipeId,
        selectedRecipeFromTupper,
        alert,
        alertTime
      )
    }
    closePopup()
  }, [
    selectedRecipeId,
    selectedRecipeFromTupper,
    alert,
    alertTime,
    storeRecipe,
    closePopup,
  ])

  useEffect(() => {
    setPopupOnOk(() => handleOk)
  }, [handleOk, setPopupOnOk])

  function sortRecipesByQty(a: recipe, b: recipe) {
    const qtyA = a.storage_qty || 0
    const qtyB = b.storage_qty || 0
    return qtyB - qtyA
  }

  function generateRecipeOptions() {
    if (!existingRecipes) return null
    return existingRecipes
      .filter(recipe => recipe[type]) // Only recipes for this meal type
      .sort(sortRecipesByQty)
      .map(optionRecipe => (
        <option key={optionRecipe.id} value={optionRecipe.id}>
          {`${optionRecipe.name}${optionRecipe.storage_qty && optionRecipe.storage_qty > 0 ? ` (${optionRecipe.storage_qty})` : ''}`}
        </option>
      ))
  }

  const handleItemSelectChange = (value: string) => {
    const id = parseInt(value, 10)
    if (!isNaN(id) && existingRecipes) {
      const selectedRecipe = existingRecipes.find(r => r.id === id)
      const comesFromTupper =
        selectedRecipe?.storage_qty !== undefined &&
        selectedRecipe?.storage_qty > 0
      setSelectedRecipeFromTupper(comesFromTupper)
      setSelectedRecipeId(selectedRecipe?.id ? selectedRecipe.id : null)

      if (comesFromTupper) {
        setAlert(getMessage('recipe_comes_from_tupper_alert'))
        if (type === 'cena') {
          setAlertTime(AlertTime.SAME_DAY_MORNING)
        } else {
          setAlertTime(AlertTime.PREVIOUS_DAY_NIGHT)
        }
      } else {
        setAlert(selectedRecipe?.alert || '')
        setAlertTime(selectedRecipe?.alertTime || AlertTime.SAME_DAY_MORNING)
      }
    }
  }

  return (
    <div
      className={`pop-up-form calendar-pop-up-main-content ${isMobile ? 'pop-up-form-mobile calendar-pop-up-main-content-mobile' : ''}`}
    >
      <div className="pop-up-form-row">
        <select
          id="calendar-recipe-select"
          className="calendar-recipe-select"
          onChange={e => handleItemSelectChange(e.target.value)}
          value={
            selectedRecipeId !== null
              ? (existingRecipes?.find(r => r.id === selectedRecipeId)?.id ??
                '')
              : ''
          }
        >
          <option value="">{getMessage('recipe_select')}</option>
          {generateRecipeOptions()}
        </select>
      </div>
      {((recipeId !== null && recipeId !== undefined && recipeId >= 1) ||
        (selectedRecipeId !== null && selectedRecipeId >= 1)) && (
        <AlertInput
          user={user}
          alert={alert}
          setAlert={setAlert}
          alertTime={alertTime}
          setAlertTime={setAlertTime}
          isMobile={isMobile}
        />
      )}
    </div>
  )
}

export default CalendarPopupContent
